/*
 * ⚠️ BU DOSYANIN IKIZI: `web_admin/src/features/badges/utils/badgeCardPdf.ts`
 *    Admin panelinden indirilen kart ile kullanicinin indirdigi kart BIREBIR
 *    AYNI olmali. Birinde degisiklik yapilirsa digeri de guncellenmeli.
 *    (Neden ortak pakete tasinmadigi admin'deki kopyanin basinda yazili.)
 *
 * FAZ 7 / F7-6 — `jspdf` DINAMIK import.
 *
 * ⚠️ Bu dosya `Header.tsx` tarafindan import ediliyor ve Header HER SAYFADA
 *    var. Yani modul tepesindeki `import { jsPDF } from 'jspdf'` jspdf'i
 *    (~350 KB) main bundle'a sokuyordu — kullanicilarin buyuk kismi hic
 *    badge PDF'i indirmiyor olsa bile.
 *
 * Artik yalnizca `buildBadgeCardPdfBlob` CAGRILDIGINDA yukleniyor. Fonksiyon
 * zaten `async`, yani cagiranlar degismedi.
 *
 * ── B2Meet 2026 fiziksel kart tasarimi ──────────────────────────────
 * Kart artik A4 sayfa icine sigdirilmis bir gorsel degil; PDF'in kendisi
 * kartin GERCEK basim olcusunde (85mm x 120mm): bkz. `CARD_WIDTH_PT` /
 * `CARD_HEIGHT_PT`. Ust basligi (etkinlik logosu/tarihi) ve alt seridi
 * (kart tipi rengi + IFM/B2Meet logolari) tasarim ekibinden gelen HAZIR
 * gorseller (`../assets/badge/*.png`) olusturuyor; bu kod yalnizca ORTADAKI
 * beyaz alana ad/firma/QR/token basiyor. Gorseldeki beyaz alanin sinirlari
 * (`ZONE_TOP_PT` / `ZONE_BOTTOM_PT`) 3 sablonun tamamindan piksel analiziyle
 * olculdu — üçünde de birebir ayni.
 */

/*
 * ── Kart tipi → arka plan gorseli ────────────────────────────────────────
 * Sadece 3 kart tipi icin ozel tasarim var (VIS/EXH/BYM). Digerleri (VIP,
 * SPK, STF, PRT, SUP — bkz. `badge-type.constants.ts`) icin ayri bir gorsel
 * YOK; bu tipler Ziyaretci sablonuna duser. Bilinçli karar: yeni bir tip eklendiginde bu
 * dosyaya dokunmadan calismaya devam etsin, tasarim geldiginde tek satir
 * eklenir.
 */
import badgeBgVisitor from '../assets/badge/badge-bg-visitor.png'
import badgeBgExhibitor from '../assets/badge/badge-bg-exhibitor.png'
import badgeBgBuyerMission from '../assets/badge/badge-bg-buyer-mission.png'

const BADGE_BACKGROUNDS: Record<string, string> = {
    VIS: badgeBgVisitor,
    EXH: badgeBgExhibitor,
    BYM: badgeBgBuyerMission,
}

const resolveBadgeBackground = (badgeType?: string) => BADGE_BACKGROUNDS[badgeType || ''] || badgeBgVisitor

/*
 * ── Tasarim (web_admin → Yaka Karti Ayarlari) ────────────────────────────
 * Sunucu her kart icin bir `design` doner: arka plan gorseli ve beyaz alan
 * yerlesimi. Arka plan yoksa ya da yuklenemezse yukaridaki hazir gorsele
 * dusulur — kart indirmesi tasarim yuzunden ASLA durmamali.
 *
 * `backgroundUrl` MUTLAK adres olmali; sunucunun dondugu goreceli yolu her
 * uygulama kendi API tabanina gore cevirir (`toBadgeCardDesign`).
 */
export type BadgeCardLayout = {
    widthMm?: number
    heightMm?: number
    zoneTopPt: number
    zoneBottomPt: number
    textColor: string
    showCompany: boolean
    showToken: boolean
}

export type BadgeCardDesign = {
    backgroundUrl?: string | null
    layout?: Partial<BadgeCardLayout> | null
}

export const resolveBadgeCardSize = (design?: BadgeCardDesign | null) => {
    const valid = (value: number | undefined, fallback: number) =>
        typeof value === 'number' && Number.isFinite(value) && value >= 50 && value <= 300 ? value : fallback
    const widthMm = valid(design?.layout?.widthMm, 85)
    const heightMm = valid(design?.layout?.heightMm, 120)
    return {
        widthMm, heightMm,
        widthPt: widthMm === 85 ? CARD_WIDTH_PT : widthMm * 72 / 25.4,
        heightPt: heightMm === 120 ? CARD_HEIGHT_PT : heightMm * 72 / 25.4,
    }
}

const resolveBadgeLayout = (design?: BadgeCardDesign | null): BadgeCardLayout => {
    const size = resolveBadgeCardSize(design)
    const layout = { ...DEFAULT_BADGE_LAYOUT, ...(design?.layout ?? {}) }
    if (!Number.isFinite(layout.zoneTopPt) || !Number.isFinite(layout.zoneBottomPt) ||
        layout.zoneTopPt < 0 || layout.zoneBottomPt > size.heightPt ||
        !(layout.zoneBottomPt - layout.zoneTopPt >= 100)) {
        const bottom = Math.min(size.heightPt, DEFAULT_BADGE_LAYOUT.zoneBottomPt * (size.heightMm / 120))
        return { ...layout, zoneTopPt: Math.max(0, Math.min(DEFAULT_BADGE_LAYOUT.zoneTopPt * (size.heightMm / 120), bottom - 100)), zoneBottomPt: bottom }
    }
    return layout
}

/*
 * ⚠️ Tasarim gorseli `fetch` → blob URL ile yukleniyor, `<img src>` ile
 * DEGIL: API baska bir origin; oradan gelen gorsel canvas'i "kirletir"
 * (tainted) ve `toDataURL` hata verir. Blob URL ayni origin sayilir.
 * Toplu ZIP indirmesinde ayni gorsel yuzlerce kez istenmesin diye bellekte
 * tutuluyor; adres `?v=` tasidigi icin gorsel degisince anahtar da degisir.
 */
const designBackgroundCache = new Map<string, Promise<string>>()

const loadDesignBackground = (url: string) => {
    let cached = designBackgroundCache.get(url)
    if (!cached) {
        cached = fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error('Tasarım görseli alınamadı')
                return response.blob()
            })
            .then((blob) => URL.createObjectURL(blob))
        cached.catch(() => designBackgroundCache.delete(url))
        designBackgroundCache.set(url, cached)
    }
    return cached
}

const loadBadgeBackground = async (badgeType?: string, design?: BadgeCardDesign | null) => {
    const url = String(design?.backgroundUrl || '').trim()
    if (url) {
        try {
            return await loadImage(await loadDesignBackground(url))
        } catch {
            // Hazir gorsele dus — asagida.
        }
    }
    return loadImage(resolveBadgeBackground(badgeType))
}

type BadgeCardPdfParams = {
    fullName: string
    companyName?: string
    fallbackCompanyName: string
    badgeType?: string
    qrToken?: string
    /**
     * QR'in icindeki public adres — `https://.../badge/{token}`. ZORUNLU.
     *
     * QR bu adresten PDF uretilirken cizilir; ekrandaki bir canvas'tan
     * KOPYALANMAZ. Onceden kopyalaniyordu ve kart indirme aninda yeni
     * olusturuldugunda canvas henuz yeniden cizilmemis oluyor, PDF'e eski
     * profil QR'i (`/user/{userId}`) basiliyordu.
     *
     * Ayrica PDF'e TIKLANABILIR baglanti olarak eklenir.
     */
    badgeUrl: string
    /** QR'in ortasina basilacak logo (opsiyonel); altindaki moduller temizlenir. */
    qrLogoSrc?: string
    /** Kartin tasarimi — yoksa tip koduna gore hazir gorsel ve varsayilan yerlesim. */
    design?: BadgeCardDesign | null
}

/** Yaka karti QR'i yalnizca kart token adresi tasiyabilir; userId asla. */
export const isBadgeQrUrl = (value: string) => /\/badge\/[A-Za-z0-9]+$/.test(value)

const QR_LOGO_RATIO = 110 / 512

const buildQrCanvas = async (value: string, sizePx: number, logoSrc?: string) => {
    const qrModule = await import('qrcode')
    const QRCode = qrModule.default ?? qrModule
    const canvas = document.createElement('canvas')
    await QRCode.toCanvas(canvas, value, {
        errorCorrectionLevel: 'H',
        margin: 4,
        width: Math.round(sizePx),
    })
    if (logoSrc) {
        const context = canvas.getContext('2d')
        const logo = await loadImage(logoSrc).catch(() => null)
        if (context && logo) {
            const logoSize = canvas.width * QR_LOGO_RATIO
            const offset = (canvas.width - logoSize) / 2
            context.fillStyle = '#ffffff'
            context.fillRect(offset, offset, logoSize, logoSize)
            context.drawImage(logo, offset, offset, logoSize, logoSize)
        }
    }
    return canvas
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('Yaka kartı arka planı yüklenemedi'))
        image.src = src
    })

const createCanvasContext = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d')
    if (!context) {
        throw new Error('Canvas context unavailable')
    }
    return context
}

// Varsayılan fiziksel ölçü: 85mm x 120mm (1mm = 2.83464567 pt).
const CARD_WIDTH_PT = 240.945
const CARD_HEIGHT_PT = 340.157

// Kanvas cozunurlugu (~300dpi baski kalitesi) — arka plan gorselleri de bu boyutta hazirlandi.
const CANVAS_WIDTH = 1004
const CANVAS_HEIGHT = 1418
const SCALE = CANVAS_WIDTH / CARD_WIDTH_PT // px / pt

/*
 * Ortadaki BEYAZ ALAN — uc sablonun tamami 300dpi'da render edilip merkez
 * sutunda piksel taranarak olculdu (ust basligin bittigi / alt seridin
 * basladigi y). Üçünde de BIREBIR ayni: 150.24pt–292.08pt, tam genislikte.
 */
const ZONE_TOP_PT = 150.24
const ZONE_BOTTOM_PT = 292.08

/* Tasarimda yerlesim verilmemisse — sunucudaki DEFAULT_BADGE_DESIGN_LAYOUT ile ayni. */
const DEFAULT_BADGE_LAYOUT: BadgeCardLayout = {
    widthMm: 85,
    heightMm: 120,
    zoneTopPt: ZONE_TOP_PT,
    zoneBottomPt: ZONE_BOTTOM_PT,
    textColor: '#101828',
    showCompany: true,
    showToken: true,
}
const ZONE_MARGIN_PT = 14
const CENTER_X_PT = CARD_WIDTH_PT / 2

const pt = (value: number) => value * SCALE

/*
 * ── Deger yerlesimi: kucult, sigmiyorsa 2 satira bol, o da sigmiyorsa kes ──
 * Beyaz alan sabit ve kucuk (85mm genislik x ~50mm yukseklik) oldugu icin
 * eski A4 tasarimindaki gibi "ne kadar uzunsa o kadar satir" yaklasimi
 * kullanilamaz — QR'in ve alttaki token/adres satirlarinin da o alanda yer
 * bulmasi gerekiyor. Bu yuzden en fazla 2 satir; sonrasi "…" ile kesilir.
 */
const ellipsize = (context: CanvasRenderingContext2D, text: string, maxWidthPx: number) => {
    if (context.measureText(text).width <= maxWidthPx) return text
    let low = 0
    let high = text.length
    while (low < high) {
        const mid = Math.ceil((low + high) / 2)
        const candidate = `${text.slice(0, mid).trimEnd()}…`
        if (context.measureText(candidate).width <= maxWidthPx) {
            low = mid
        } else {
            high = mid - 1
        }
    }
    return `${text.slice(0, low).trimEnd()}…`
}

const fitValueLines = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidthPx: number,
    maxFontPx: number,
    minFontPx: number,
) => {
    for (let fontSize = maxFontPx; fontSize >= minFontPx; fontSize -= pt(0.5)) {
        context.font = `700 ${fontSize}px Arial, sans-serif`
        if (context.measureText(text).width <= maxWidthPx) {
            return { lines: [text], fontSize }
        }
    }

    context.font = `700 ${minFontPx}px Arial, sans-serif`
    const words = text.split(/\s+/).filter(Boolean)
    let split: [string, string] | null = null
    for (let cut = 1; cut < words.length; cut += 1) {
        const line1 = words.slice(0, cut).join(' ')
        const line2 = words.slice(cut).join(' ')
        if (
            context.measureText(line1).width <= maxWidthPx &&
            context.measureText(line2).width <= maxWidthPx
        ) {
            split = [line1, line2]
            break
        }
    }
    if (!split) {
        const mid = Math.ceil(words.length / 2) || 1
        split = [words.slice(0, mid).join(' ') || text, words.slice(mid).join(' ')]
    }

    const lines = split
        .filter((line) => line.length > 0)
        .map((line) => ellipsize(context, line, maxWidthPx))

    return { lines, fontSize: minFontPx }
}

const drawCentered = (
    context: CanvasRenderingContext2D,
    text: string,
    baselineY: number,
    centerXPt = CENTER_X_PT,
) => {
    context.textAlign = 'center'
    context.fillText(text, pt(centerXPt), baselineY)
}

export const buildBadgeCardPdfBlob = async ({
    fullName,
    companyName,
    fallbackCompanyName,
    badgeType,
    qrToken,
    badgeUrl,
    qrLogoSrc,
    design,
}: BadgeCardPdfParams) => {
    const normalizedFullName = String(fullName || '').trim()
    const normalizedCompanyName = String(companyName || '').trim()
    const normalizedToken = String(qrToken || '').trim()
    const normalizedBadgeUrl = String(badgeUrl || '').trim()

    if (!normalizedFullName) {
        throw new Error('Missing full name')
    }

    if (!isBadgeQrUrl(normalizedBadgeUrl)) {
        throw new Error('Missing badge URL')
    }

    const layout = resolveBadgeLayout(design)
    const size = resolveBadgeCardSize(design)
    const zoneHeightPt = layout.zoneBottomPt - layout.zoneTopPt
    const showCompany = layout.showCompany
    const showToken = layout.showToken && Boolean(normalizedToken)

    const backgroundImage = await loadBadgeBackground(badgeType, design)

    const badgeCanvas = document.createElement('canvas')
    badgeCanvas.width = size.widthMm === 85 ? CANVAS_WIDTH : Math.round(pt(size.widthPt))
    badgeCanvas.height = size.heightMm === 120 ? CANVAS_HEIGHT : Math.round(pt(size.heightPt))

    const context = createCanvasContext(badgeCanvas)
    context.drawImage(backgroundImage, 0, 0, badgeCanvas.width, badgeCanvas.height)

    const maxTextWidthPx = pt(size.widthPt - ZONE_MARGIN_PT * 2)

    const nameFit = fitValueLines(context, normalizedFullName, maxTextWidthPx, pt(12), pt(9))
    const companyFit = fitValueLines(context, normalizedCompanyName, maxTextWidthPx, pt(10), pt(8))

    const nameLinePx = nameFit.fontSize * 1.2
    const companyLinePx = companyFit.fontSize * 1.2

    const padTopPx = pt(10)
    // Gizlenen satirin alani da bosalir; kalan yer QR'a aktarilir (qrMaxPx'e kadar).
    const gapNameToCompanyPx = showCompany ? pt(6) : 0
    const gapCompanyToQrPx = pt(8)
    const gapQrToTokenPx = showToken ? pt(6) : 0
    const tokenFontPx = pt(7)
    const tokenLinePx = showToken ? pt(10) : 0
    const padBottomPx = pt(6)

    const nameBlockPx = nameLinePx * nameFit.lines.length
    const companyBlockPx = showCompany ? companyLinePx * companyFit.lines.length : 0

    const fixedHeightPx =
        padTopPx +
        nameBlockPx +
        gapNameToCompanyPx +
        companyBlockPx +
        gapCompanyToQrPx +
        gapQrToTokenPx +
        tokenLinePx +
        padBottomPx

    /*
     * Etiketler ("Ad Soyad / Full Name", "Firma / Company", "Yaka Kartı QR /
     * Badge QR") ve gorunur adres satiri kaldirildi — kart daha sade
     * gorunsun diye. Bosalan alan QR'a aktarildi (bkz. qrMinPx/qrMaxPx
     * artisi) ve ad/firma yazi boyutu kucultuldu.
     */
    const qrMinPx = pt(52)
    const qrMaxPx = pt(70)
    const qrSizePx = Math.max(qrMinPx, Math.min(qrMaxPx, pt(zoneHeightPt) - fixedHeightPx))

    let y = pt(layout.zoneTopPt) + padTopPx

    context.fillStyle = layout.textColor
    context.font = `700 ${nameFit.fontSize}px Arial, sans-serif`
    nameFit.lines.forEach((line) => {
        y += nameLinePx * 0.75
        drawCentered(context, line, y, size.widthPt / 2)
        y += nameLinePx * 0.25
    })

    y += gapNameToCompanyPx

    if (showCompany) {
        context.fillStyle = layout.textColor
        context.font = `700 ${companyFit.fontSize}px Arial, sans-serif`
        companyFit.lines.forEach((line) => {
            y += companyLinePx * 0.75
            drawCentered(context, line, y, size.widthPt / 2)
            y += companyLinePx * 0.25
        })
    }

    y += gapCompanyToQrPx

    const qrXPx = pt(size.widthPt / 2) - qrSizePx / 2
    const qrYPx = y
    const qrCanvas = await buildQrCanvas(normalizedBadgeUrl, qrSizePx, qrLogoSrc)
    context.drawImage(qrCanvas, qrXPx, qrYPx, qrSizePx, qrSizePx)
    y += qrSizePx + gapQrToTokenPx

    /*
     * Token METIN olarak da basiliyor. Sebep: QR okunamadiginda (kart kirisik,
     * kamera bozuk, isik yetersiz) gorevli 15 karakteri elle girebilmeli.
     */
    let tokenY = y
    if (showToken) {
        context.fillStyle = layout.textColor
        context.font = `700 ${tokenFontPx}px "Courier New", monospace`
        tokenY = y + tokenLinePx * 0.75
        drawCentered(context, normalizedToken, tokenY, size.widthPt / 2)
    }
    y += tokenLinePx

    const imageData = badgeCanvas.toDataURL('image/png')
    // Dinamik yukleme — ilk cagrida indirilir, sonra tarayici cache'ler.
    const { jsPDF } = await import('jspdf');
    const pdf = new jsPDF({
        orientation: size.widthPt > size.heightPt ? 'landscape' : 'portrait',
        unit: 'pt',
        format: [size.widthPt, size.heightPt],
    })

    pdf.addImage(imageData, 'PNG', 0, 0, size.widthPt, size.heightPt, undefined, 'FAST')

    /*
     * ── Tiklanabilir baglanti ────────────────────────────────────────────
     * Sayfanin tamami TEK BIR PNG olarak basiliyor, yani icindeki hicbir sey
     * kendiliginden tiklanabilir degil. Baglantiyi jsPDF'in annotation
     * katmaniyla gorselin UZERINE bindiriyoruz.
     *
     * Kanvas → sayfa olcegi SCALE ile sabit; kanvas boyutu degisirse
     * baglanti alani da birlikte kaysin diye oranla hesaplaniyor.
     *
     * Iki alan tiklanabilir: QR'in kendisi ve token metni. Gorunur adres
     * satiri kaldirildiginda o alanin tiklama bolgesi de kaldirildi.
     * Kullanici hangisine basarsa bassin ayni yere gitmeli.
     */
    if (normalizedBadgeUrl) {
        const toPt = (value: number) => value / SCALE
        const link = (x: number, y1: number, width: number, height: number) => {
            pdf.link(toPt(x), toPt(y1), toPt(width), toPt(height), { url: normalizedBadgeUrl })
        }

        link(qrXPx, qrYPx, qrSizePx, qrSizePx)

        // Metin satirlari taban cizgisinden cizildigi icin tiklama alani
        // satirin biraz USTUNDEN baslatiliyor.
        if (showToken) {
            link(pt(size.widthPt / 2) - maxTextWidthPx / 2, tokenY - tokenLinePx, maxTextWidthPx, tokenLinePx * 1.4)
        }
    }

    return pdf.output('blob')
}
